import React, { useState, useEffect, useRef } from "react";
import EnterpriseDeletedAssessments from "./EnterpriseDeletedAssessments";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import { useURLState } from "../hooks/useURLState";
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Card from "../components/Card";
import Button from "../components/Buttons";
import LoadingSpinner from "../components/LoadingSpinner";
import { Plus, FileText, Shield, Trash2, RefreshCw, Upload, Lock, Users, Eye } from "lucide-react";
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import DealAssignAssessModal from "../components/DealAssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import EnterpriseAssessmentHistoryMenu, { ReassessmentNote } from "./EnterpriseAssessmentHistoryMenu";
import EnterpriseNewDealModal from "../components/EnterpriseNewDealModal";
import { useVerticals } from "../contexts/VerticalsContext";
import { useOrganizations } from "../contexts/OrganizationContext";
import { useRBAC } from "../hooks/useRBAC";
import AssessmentActivityLog from "../components/AssessmentActivityLog";
import { severityPostureScore } from '../utils/riskScoringEngine';

const EnterpriseDeals = () => {
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [pageSize, setPageSize] = usePageSize('enterpriseDeals');
  const listTopRef = useRef(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [reassessError, setReassessError] = useState(null);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { verticals } = useVerticals();
  const organizations = useOrganizations();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canManageDeal, canViewDeal, canAssessViaAssignment, canCreateDealOrBusinessUnit, authVersion, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageRisksForDeal = (dealID) => canManageRisks || canManageDeal(dealID);
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);

  const handleDealSelected = (deal) => {
    setShowNewDealModal(false);
    setCurrentPageParam(1);
    fetchDeals();
  };

  // Bumped after each delete so an open "Deleted assessments" list reloads.
  const [deletedVersion, setDeletedVersion] = useState(0);

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiCall('DELETE', `/enterpriseAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      setDeletedVersion((v) => v + 1);
      await fetchDeals();
    } catch (error) {
      setDeleteError(error?.response?.data?.message || 'Could not delete this assessment.');
    } finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    setReassessError(null);
    try {
      const result = await apiCall('POST', `/enterpriseAssessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      // Reassess now creates a NEW assessment row (the old one is preserved as read-only history)
      // — navigate to the new id, not the one that was reassessed.
      navigate(`/EnterpriseAssessment/${result?.newAssessmentId ?? assessmentToReassess}?step=review`);
    } catch (error) {
      setReassessError(error?.response?.data?.message || 'Could not reassess this assessment.');
    } finally {
      setReassessing(false);
    }
  };

  // Fetch Enterprise deals with risk summary from optimized endpoint
  const fetchDeals = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        setLoading(false);
        return;
      }

      const data = await apiCall("GET", `/deals/summary?category=Enterprise&page=${currentPage}&pageSize=${pageSize}`);

      const dealsWithRiskData = (data.items || []).map((deal) => ({
        dealID: deal.dealId,
        isPrivate: deal.isPrivate,
        dealName: deal.dealName,
        verticalId: deal.verticalId,
        domain: deal.domain,
        targetEndDate: deal.targetEndDate,
        hasAssessment: deal.applicationCount > 0,
        riskCounts: {
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        },
        totalRisks: (deal.riskCounts?.critical || 0) + (deal.riskCounts?.high || 0) +
                    (deal.riskCounts?.medium || 0) + (deal.riskCounts?.low || 0),
        // active (non-completed) count for the View Risks label; total keeps gating the button
        activeRisks: deal.activeRisks,
        riskScore: calculateRiskScore({
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        }),
        riskLevel: deal.riskLevel === "NONE" ? "No Risk" : deal.riskLevel,
        latestAssessment: deal.assessmentId ? {
          enterpriseAssessmentID: deal.assessmentId,
          enterpriseAssignedToUserId: deal.assignedToUserId,
          enterpriseAssignedToUserName: deal.assignedToUserName,
          enterpriseStatus: deal.status,
          assigneeUserIds: deal.assigneeUserIds,
          hasAnswers: deal.assessmentHasAnswers,
          // Enterprise reassessment: View Risks opens the round whose risks are live (the previous
          // one while the reassessment is unfinished); History lists superseded rounds.
          riskAssessmentId: deal.riskAssessmentId ?? deal.assessmentId,
          riskAssessmentStatus: deal.riskAssessmentStatus ?? deal.status,
          reassessmentStatus: deal.reassessmentStatus,
          hasHistory: deal.hasHistory,
        } : null,
      }));

      // Backend /deals/summary already applies RBAC deal-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for deal-scoped users by clobbering totalCount with the current page's length.
      setDeals(dealsWithRiskData);
      setTotalCount(data.totalCount || 0);
    } catch (err) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const calculateRiskScore = (riskCounts) => {
    return severityPostureScore(riskCounts);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    fetchDeals();
  }, [currentPage, pageSize, authVersion]);

  // Auto-select from navigation state
  useEffect(() => {
    const autoSelectId = location.state?.selectedDealID;
    if (autoSelectId && deals.length > 0) {
      const target = deals.find((d) => d.dealID === autoSelectId);
      if (target) {
        setSelectedDeal(target);
      }
    }
  }, [deals, location.state]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">
            <span className="text-blue-800">Enterprise Deals Management</span>
          </h1>
          <p className="mt-2 text-gray-600">Manage Enterprise deals for risk assessment</p>
        </div>

        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Enterprise Deals</h2>
              {canAddDealOrBU && (
                <Button size="sm" onClick={() => setShowNewDealModal(true)}>
                  <Plus className="h-4 w-4 mr-2" /> New Deal
                </Button>
              )}
            {showNewDealModal && (
              <EnterpriseNewDealModal
                isOpen={showNewDealModal}
                onClose={() => setShowNewDealModal(false)}
                onDealSelected={handleDealSelected}
                existingDealIds={deals.map(d => d.dealID)}
              />
            )}
          </div>

          <div className="space-y-3" ref={listTopRef}>
            {deals.length === 0 && (
              <p className="text-gray-500 text-center py-4">No Enterprise deals found. Click 'New Deal' to get started.</p>
            )}
            {deals.map((deal) => (
              <div
                key={deal.dealID}
                className="p-4 border-2 rounded-lg border-gray-200"
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-content-primary">{deal.dealName}{deal.isPrivate && <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded align-middle"><Lock className="h-3 w-3" />Private</span>}<AssessmentStatusBadge status={deal.latestAssessment?.enterpriseStatus} /></h3>
                        <p className="text-sm text-gray-600">
                          {verticals.find((v) => v.verticalId === deal.verticalId)?.verticalName || "N/A"}
                        </p>
                        {deal.description && <p className="text-sm text-gray-500 mt-1">{deal.description}</p>}
                        {deal.latestAssessment?.enterpriseAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{deal.latestAssessment.enterpriseAssignedToUserName}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Risk details */}
                    {deal.hasAssessment ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {deal.riskCounts?.CRITICAL > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{deal.riskCounts.CRITICAL} Critical</span>}
                          {deal.riskCounts?.HIGH > 0 && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{deal.riskCounts.HIGH} High</span>}
                          {deal.riskCounts?.MEDIUM > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{deal.riskCounts.MEDIUM} Medium</span>}
                          {deal.riskCounts?.LOW > 0 && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{deal.riskCounts.LOW} Low</span>}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Risk Score: {deal.riskScore || 0}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${deal.riskScore <= 40 ? "bg-green-500" : deal.riskScore <= 60 ? "bg-yellow-500" : deal.riskScore <= 80 ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${deal.riskScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No Assessment Data</div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                  {canManageRisksForDeal(deal.dealID) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={deal.hasAssessment && deal.latestAssessment?.enterpriseStatus === "completed"}
                      title={deal.hasAssessment && deal.latestAssessment?.enterpriseStatus === "completed" ? "Assessment is completed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntityForAssign(deal);
                        setShowAssignModal(true);
                      }}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {deal.latestAssessment?.enterpriseAssignedToUserId ? "Reassign" : "Assign"}
                    </Button>
                  )}

                  {canManageRisksForDeal(deal.dealID) && deal.latestAssessment?.enterpriseAssessmentID && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Manage additional respondents for this assessment"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRespondentsAssessmentId(deal.latestAssessment.enterpriseAssessmentID);
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Respondents
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={
                      !canAssess(deal.latestAssessment?.enterpriseAssignedToUserId, null, 'DEAL', deal.dealID, deal.latestAssessment?.assigneeUserIds || []) ||
                      (deal.hasAssessment && (deal.latestAssessment?.enterpriseStatus === "completed" || deal.latestAssessment?.enterpriseStatus === "analyzing"))
                    }
                    title={
                      (deal.latestAssessment?.enterpriseStatus === "completed" ? "Assessment is completed"
                        : deal.latestAssessment?.enterpriseStatus === "analyzing" ? "Assessment is being analyzed"
                        : "")
                        || getAssessDisabledReason(deal.latestAssessment?.enterpriseAssignedToUserId, null, 'DEAL', deal.dealID, deal.latestAssessment?.assigneeUserIds || [])
                        || ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!deal.latestAssessment?.enterpriseAssignedToUserId) {
                        setShowUnassignedAlert(true);
                        return;
                      }
                      const entPath = deal.latestAssessment?.enterpriseAssessmentID
                        ? `/EnterpriseAssessment/${deal.latestAssessment.enterpriseAssessmentID}`
                        : "/EnterpriseAssessment";
                      navigate(entPath, {
                        state: {
                          dealID: deal.dealID,
                          dealName: deal.dealName,
                          organizationId: deal.organizationId,
                          verticalId: deal.verticalId,
                          regionId: deal.regionId,
                          subVerticalId: deal.subVerticalId,
                        },
                      });
                    }}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Assess
                  </Button>

                  {/* hasAnswers: nothing to show without answered questions (e.g. document-derived
                      assessments). Viewing mirrors the backend's GetViewableAssessmentAsync: deal
                      scope or assignment. */}
                  {deal.hasAssessment && deal.latestAssessment?.enterpriseAssessmentID && deal.latestAssessment?.enterpriseStatus === "completed" &&
                    deal.latestAssessment?.hasAnswers &&
                    (canViewDeal(deal.dealID) || canAssessViaAssignment(deal.latestAssessment?.enterpriseAssignedToUserId, deal.latestAssessment?.assigneeUserIds || [])) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/EnterpriseAssessment/${deal.latestAssessment.enterpriseAssessmentID}?step=review`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Answers
                    </Button>
                  )}

                  {canManageRisksForDeal(deal.dealID) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/EnterpriseDocumentAssessment", {
                          state: {
                            dealId: deal.dealID,
                            dealName: deal.dealName,
                            organizationId: deal.organizationId,
                            verticalId: deal.verticalId,
                            regionId: deal.regionId,
                            subVerticalId: deal.subVerticalId,
                          },
                        });
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Assess Document
                    </Button>
                  )}

                  {deal.hasAssessment && deal.latestAssessment?.enterpriseAssessmentID ? (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      disabled={String(deal.latestAssessment?.riskAssessmentStatus ?? '').toLowerCase() !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/EnterpriseAssessments/${deal.latestAssessment.riskAssessmentId}/risks`);
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      {deal.totalRisks === 0
                        ? 'View Results'
                        : `View Risks (${deal.activeRisks ?? deal.totalRisks})`}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      disabled
                      title="No assessment data available"
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks (0)
                    </Button>
                  )}

                  <EnterpriseAssessmentHistoryMenu
                    assessmentId={deal.latestAssessment?.enterpriseAssessmentID}
                    hasHistory={deal.latestAssessment?.hasHistory} />

                  {canManageOrganizationRisks() && deal.latestAssessment?.enterpriseAssessmentID && (
                    <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                      disabled={deal.latestAssessment?.enterpriseStatus !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToReassess(deal.latestAssessment.enterpriseAssessmentID);
                        setShowReassessModal(true);
                      }}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reassess
                    </Button>
                  )}

                  {canDeleteAssessment && deal.latestAssessment?.enterpriseAssessmentID && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="px-1 py-1 h-9 text-xs"
                      disabled={deal.latestAssessment?.enterpriseStatus === "analyzing"}
                      title={deal.latestAssessment?.enterpriseStatus === "analyzing" ? "Assessment is being analyzed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteError(null);
                        setAssessmentToDelete(deal.latestAssessment.enterpriseAssessmentID);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                    </div>
                    <ReassessmentNote status={deal.latestAssessment?.reassessmentStatus} />
                  </div>
                  {deal.latestAssessment?.enterpriseAssessmentID && (
                    <>
                      <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                      <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                        <AssessmentActivityLog assessmentType="enterprise" assessmentId={String(deal.latestAssessment.enterpriseAssessmentID)} hideQuestionEvents includeChain />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPageParam}
            onPageSizeChange={setPageSize}
            scrollTargetRef={listTopRef}
            busy={paging}
          />
        </Card>

        <EnterpriseDeletedAssessments level="deal" refreshKey={deletedVersion} />

        {showAssignModal && selectedEntityForAssign && (
          <DealAssignAssessModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            dealID={selectedEntityForAssign.dealID}
            isPrivate={selectedEntityForAssign.isPrivate}
            assessmentType="enterprise"
            organizationId={JSON.parse(localStorage.getItem("selectedOrganization"))?.organizationId}
            hasExistingAssessment={selectedEntityForAssign.hasAssessment}
            refreshData={fetchDeals}
          />
        )}

        <AssessmentRespondentsModal
          isOpen={respondentsAssessmentId != null}
          onClose={() => setRespondentsAssessmentId(null)}
          assessmentType="enterprise"
          assessmentId={respondentsAssessmentId}
          refreshData={fetchDeals}
        />

        <DeleteAssessmentModal
          isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); setDeleteError(null); }}
          onConfirm={handleDeleteAssessment}
          isDeleting={deleting}
          error={deleteError}
        />

        <ReassessModal
          isOpen={showReassessModal}
          onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); setReassessError(null); }}
          onConfirm={handleReassessAssessment}
          isReassessing={reassessing}
          errorMessage={reassessError}
          previousStaysLive
        />

        <AssessmentUnassignedModal
          isOpen={showUnassignedAlert}
          onClose={() => setShowUnassignedAlert(false)}
        />
      </div>
    </div>
  );
};

export default EnterpriseDeals;
